import dayjs from 'dayjs';

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'] as const;

export type DateFormatStyle = 'slash' | 'hyphen' | 'japanese';

export interface FormatDateOptions {
  showWeekday?: boolean;
  showTime?: boolean;
  showSeconds?: boolean;
  format?: DateFormatStyle;
}

type DateInput = Date | string | number | null | undefined;

/**
 * 日付を統一フォーマットで文字列化する
 * @param value - 日付値（Date, ISO文字列, タイムスタンプ, null, undefined）
 * @param options - フォーマットオプション
 * @returns フォーマットされた日付文字列、無効な値の場合はnull
 *
 * @example
 * formatDate(new Date())                              // '2024/12/05 (木)'
 * formatDate(date, { showTime: true })                // '2024/12/05 (木) 14:30'
 * formatDate(date, { showWeekday: false })            // '2024/12/05'
 * formatDate(date, { format: 'japanese' })            // '2024年12月5日 (木)'
 * formatDate(date, { showTime: true, showSeconds: true }) // '2024/12/05 (木) 14:30:45'
 */
export function formatDate(value: DateInput, options: FormatDateOptions = {}): string | null {
  const {
    showWeekday = true,
    showTime = false,
    showSeconds = false,
    format = 'slash',
  } = options;

  if (value === null || value === undefined) {
    return null;
  }

  const date = dayjs(value);
  if (!date.isValid()) {
    return null;
  }

  const weekday = WEEKDAY_LABELS[date.day()];

  let dateStr: string;
  switch (format) {
    case 'hyphen':
      dateStr = date.format('YYYY-MM-DD');
      break;
    case 'japanese':
      dateStr = date.format('YYYY年M月D日');
      break;
    case 'slash':
    default:
      dateStr = date.format('YYYY/MM/DD');
      break;
  }

  if (showWeekday) {
    dateStr += ` (${weekday})`;
  }

  if (showTime) {
    const timeFormat = showSeconds ? 'HH:mm:ss' : 'HH:mm';
    dateStr += ` ${date.format(timeFormat)}`;
  }

  return dateStr;
}

/**
 * 日付を統一フォーマットで文字列化する（フォールバック付き）
 * @param value - 日付値
 * @param options - フォーマットオプション
 * @param fallback - 無効な値の場合の代替文字列（デフォルト: '-'）
 * @returns フォーマットされた日付文字列
 */
export function formatDateWithFallback(
  value: DateInput,
  options: FormatDateOptions = {},
  fallback = '-'
): string {
  return formatDate(value, options) ?? fallback;
}

/**
 * 曜日を取得する
 * @param value - 日付値
 * @returns 曜日（日本語）、無効な値の場合はnull
 */
export function getWeekdayLabel(value: DateInput): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const date = dayjs(value);
  if (!date.isValid()) {
    return null;
  }
  return WEEKDAY_LABELS[date.day()];
}

export { WEEKDAY_LABELS };
